import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, Users, ShoppingCart, TrendingUp } from 'lucide-react';
import ModuleAnalyticsSummaryPage from '../../shared/components/analytics/ModuleAnalyticsSummaryPage';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';
import { formatChartPeriodLabel } from '../../shared/utils/chartFormatters';
import {
  transformPurchasesData,
  groupPurchasesByProduct,
  groupPurchasesByPeriod,
  groupPurchasesBySupplier,
  calculatePurchasesStats,
} from '../../shared/utils/purchasesDataUtils';
import { isPurchaseAnalyticsDocument } from '../../shared/utils/documentAnalyticsFilters';
import { useDocuments } from '../../core/contexts/DocumentsContext';
import { useProducts } from '../../core/contexts/ProductsContext';
import { useSaudiDateFilter } from '../../products/saudi/context/SaudiDateFilterContext';
import { analyticsGroupTypeForRange, isDateInRange } from '../../products/saudi/utils/dateRangePresets';

const TREND_SLICE = 12;

export default function AnalysesRapportsAchatsPage() {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.achats.pages.analysesRapportsAchatsPage.${key}`, opts);
  const { range } = useSaudiDateFilter();

  const { documents, refreshData, loading } = useDocuments();
  const { products } = useProducts();

  const purchasesData = useMemo(
    () => (documents || []).filter(isPurchaseAnalyticsDocument),
    [documents],
  );

  const transformedPurchases = useMemo(
    () => transformPurchasesData(purchasesData, [], i18n.language),
    [purchasesData, i18n.language],
  );

  const filteredPurchases = useMemo(
    () => transformedPurchases.filter((purchase) => isDateInRange(purchase.rawDate, range)),
    [transformedPurchases, range],
  );

  const groupType = useMemo(() => analyticsGroupTypeForRange(range), [range]);

  const purchasesByProduct = useMemo(
    () => groupPurchasesByProduct(filteredPurchases, products),
    [filteredPurchases, products],
  );

  const purchasesByPeriod = useMemo(
    () => groupPurchasesByPeriod(filteredPurchases, groupType),
    [filteredPurchases, groupType],
  );

  const purchaseStats = useMemo(
    () => calculatePurchasesStats(filteredPurchases),
    [filteredPurchases],
  );

  const totals = useMemo(() => {
    const totalQuantity = purchasesByProduct.reduce((sum, item) => sum + (item.quantityPurchased || 0), 0);
    const uniqueSuppliers = new Set(
      filteredPurchases.map((item) => item.supplierId || item.supplierName).filter(Boolean),
    ).size;
    return { totalQuantity, uniqueSuppliers };
  }, [purchasesByProduct, filteredPurchases]);

  const findProductCode = useCallback(
    (name) => {
      const match = products.find((pr) => pr.name === name);
      return match?.reference || match?.code || '—';
    },
    [products],
  );

  const stats = useMemo(
    () => [
      {
        label: p('kpi_total_spend'),
        value: formatBrandMoney(purchaseStats.totalAmount),
        icon: DollarSign,
        variant: 'forest',
      },
      {
        label: p('kpi_unique_suppliers'),
        value: totals.uniqueSuppliers,
        icon: Users,
        variant: 'copper',
      },
      {
        label: p('kpi_avg_order_value'),
        value: formatBrandMoney(purchaseStats.averageOrderValue),
        icon: TrendingUp,
        variant: 'forest',
      },
      {
        label: p('kpi_total_quantity'),
        value: totals.totalQuantity,
        icon: ShoppingCart,
        variant: 'copper',
      },
    ],
    [purchaseStats, totals, t],
  );

  const insights = useMemo(() => {
    if (!filteredPurchases.length) return [];

    const topSupplier = groupPurchasesBySupplier(filteredPurchases)[0];
    const topProduct = purchasesByProduct[0];
    const peakPeriod = [...purchasesByPeriod].sort((a, b) => b.totalAmount - a.totalAmount)[0];
    const items = [];

    if (topSupplier) {
      items.push({
        label: p('insight_top_supplier'),
        value: topSupplier.supplier,
        hint: formatBrandMoney(topSupplier.totalAmount),
      });
    }
    if (topProduct) {
      items.push({
        label: p('insight_top_product'),
        value: topProduct.product,
        hint: formatBrandMoney(topProduct.totalAmount),
      });
    }
    if (peakPeriod) {
      items.push({
        label: p('insight_peak_period'),
        value: formatChartPeriodLabel(peakPeriod.period, p, { fallback: peakPeriod.period }),
        hint: formatBrandMoney(peakPeriod.totalAmount),
      });
    }
    items.push({
      label: p('insight_orders_count'),
      value: String(purchaseStats.totalPurchases),
      hint: p('insight_orders_hint', { received: purchaseStats.receivedPurchases }),
    });

    return items.slice(0, 4);
  }, [filteredPurchases, purchasesByProduct, purchasesByPeriod, purchaseStats, t]);

  const charts = useMemo(() => {
    const active = purchaseStats.receivedPurchases + purchaseStats.orderedPurchases;
    const inactive = purchaseStats.pendingPurchases + purchaseStats.cancelledPurchases;

    return [
      {
        title: p('analysis_trends_title'),
        subtitle: p('analysis_trends_subtitle'),
        rows: purchasesByPeriod.slice(-TREND_SLICE).map((row) => ({
          label: formatChartPeriodLabel(row.period, p, { fallback: row.period }),
          value: Number(row.totalAmount ?? 0),
          display: formatBrandMoney(row.totalAmount ?? 0),
        })),
      },
      {
        title: p('chart_top_products_title'),
        subtitle: p('chart_top_products_subtitle'),
        variant: 'ranked',
        rows: purchasesByProduct.slice(0, 8).map((row) => ({
          label: row.product,
          value: row.totalAmount || 0,
          display: formatBrandMoney(row.totalAmount || 0),
        })),
      },
      {
        title: p('chart_status_title'),
        subtitle: p('chart_status_subtitle'),
        variant: 'status',
        active,
        inactive,
      },
    ];
  }, [purchasesByPeriod, purchasesByProduct, purchaseStats, p]);

  const table = useMemo(
    () => ({
      title: p('report_articles_title'),
      columns: [
        { key: 'code', label: p('col_code') },
        { key: 'name', label: p('col_designation') },
        { key: 'quantity', label: p('col_qty_purchased') },
        { key: 'unitPrice', label: p('col_unit_price') },
        { key: 'total', label: p('col_total_amount') },
      ],
      rows: purchasesByProduct.map((item) => ({
        code: findProductCode(item.product),
        name: item.product,
        quantity: String(item.quantityPurchased || 0),
        unitPrice: formatBrandMoney(item.averagePrice || 0),
        total: formatBrandMoney(item.totalAmount || 0),
      })),
    }),
    [purchasesByProduct, findProductCode, t],
  );

  const handleRefresh = useCallback(async () => {
    await refreshData?.();
  }, [refreshData]);

  return (
    <ModuleAnalyticsSummaryPage
      title={p('title')}
      subtitle={p('subtitle')}
      loading={loading}
      onRefresh={handleRefresh}
      stats={stats}
      insights={insights}
      charts={charts}
      table={table}
      exportFilePrefix="achats_analytics"
      emptyLabel={p('no_data')}
    />
  );
}
